import { useCallback, useEffect, useRef, useState } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { HistoricoView } from '@/components/Precificacao/HistoricoView';
import { NovaVersaoForm } from '@/components/Precificacao/NovaVersaoForm';
import { mapPrecificacaoError } from '@/components/Precificacao/messages';
import { Blueprint, Select } from '@/components/ui';
import {
  type BancoResumo,
  getHistorico,
  listBancos,
  PrecificacaoError,
  type PrecificacaoErrorReason,
  type PrecificacaoHistorico,
} from '@/lib/precificacao';

function _reasonOf(err: unknown): PrecificacaoErrorReason | null {
  return err instanceof PrecificacaoError ? err.reason : null;
}

export function PrecificacaoPage() {
  const apiClient = useApiClient();
  const [bancos, setBancos] = useState<BancoResumo[] | null>(null);
  const [bancosError, setBancosError] = useState<string | null>(null);
  const [selectedBanco, setSelectedBanco] = useState('');
  const [historico, setHistorico] = useState<PrecificacaoHistorico | null>(null);
  const [histLoading, setHistLoading] = useState(false);
  const [histError, setHistError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // A criação é append-only e parte do banco selecionado no clique. Enquanto o
  // POST está em voo o seletor fica trancado: sem isso a tela passa a mostrar
  // outra instituição enquanto a escrita persiste na anterior (F-E51.F4-06).
  const [salvando, setSalvando] = useState(false);
  // Cada carga do histórico ganha um número crescente: só a mais recente pode
  // escrever no estado. Sem isso, a resposta lenta do banco anterior chega
  // depois da nova e a tela mostra o preço de OUTRA instituição sob o seletor
  // já trocado — contexto errado para criar uma versão comercial (F-E51.F4-02).
  const pedidoRef = useRef(0);

  useEffect(() => {
    let active = true;
    listBancos(apiClient)
      .then((list) => {
        if (!active) return;
        setBancos(list);
        setBancosError(null);
        const first = list[0];
        if (first !== undefined) setSelectedBanco((cur) => cur || first.id);
      })
      .catch((err) => {
        if (!active) return;
        setBancos([]);
        const reason = _reasonOf(err);
        setBancosError(
          reason === null ? ptBR.precificacao.bancosError : mapPrecificacaoError(reason),
        );
      });
    return () => {
      active = false;
    };
  }, [apiClient]);

  const refreshHistorico = useCallback(async () => {
    const pedido = pedidoRef.current + 1;
    pedidoRef.current = pedido;
    setHistorico(null);
    if (selectedBanco === '') {
      return;
    }
    setHistLoading(true);
    setHistError(null);
    try {
      const carregado = await getHistorico(apiClient, selectedBanco);
      if (pedido !== pedidoRef.current) return;
      // Defesa em profundidade: mesmo sendo o pedido corrente, só exibe o que
      // pertence ao banco selecionado.
      if (carregado.banco_id !== selectedBanco) return;
      setHistorico(carregado);
    } catch (err) {
      if (pedido !== pedidoRef.current) return;
      const reason = _reasonOf(err);
      setHistError(
        reason === null ? ptBR.precificacao.historicoError : mapPrecificacaoError(reason),
      );
      setHistorico(null);
    } finally {
      if (pedido === pedidoRef.current) setHistLoading(false);
    }
  }, [apiClient, selectedBanco]);

  useEffect(() => {
    void refreshHistorico();
  }, [refreshHistorico]);

  function handleCreated() {
    setSuccess(ptBR.precificacao.saveSuccess);
    void refreshHistorico();
  }

  return (
    <section className="mx-auto flex w-full max-w-3xl flex-col gap-5 p-6">
      <header>
        <p className="mb-1.5 font-titulo text-rotulo uppercase text-fg-dim">
          {ptBR.precificacao.eyebrow}
        </p>
        <h1 className="text-display-md text-fg">{ptBR.precificacao.pageTitle}</h1>
        <p className="mt-1 text-body-md text-fg-muted">{ptBR.precificacao.intro}</p>
      </header>

      {bancos === null ? (
        <p data-testid="bancos-loading" className="text-body-md text-fg-muted">
          {ptBR.precificacao.bancosLoading}
        </p>
      ) : bancosError !== null ? (
        <p data-testid="bancos-error" className="text-body-md text-danger-700">
          {bancosError}
        </p>
      ) : bancos.length === 0 ? (
        <Blueprint data-testid="bancos-empty" className="p-sc-4">
          <p className="text-body-md font-medium text-fg">{ptBR.precificacao.bancosEmptyTitle}</p>
          <p className="mt-1 text-body-sm text-fg-muted">{ptBR.precificacao.bancosEmptyHint}</p>
        </Blueprint>
      ) : (
        <>
          <label className="flex max-w-sm flex-col gap-1 text-body-sm text-fg-muted">
            <span>{ptBR.precificacao.bancoLabel}</span>
            <Select
              value={selectedBanco}
              disabled={salvando}
              onChange={(e) => {
                setSelectedBanco(e.target.value);
                setSuccess(null);
              }}
              data-testid="banco-select"
            >
              {bancos.map((banco) => (
                <option key={banco.id} value={banco.id}>
                  {banco.nome}
                </option>
              ))}
            </Select>
          </label>

          {success !== null ? (
            <p role="status" data-testid="action-success" className="text-body-sm text-accent-700">
              {success}
            </p>
          ) : null}

          <div className="flex flex-col gap-2">
            <h2 className="text-heading-sm text-fg">{ptBR.precificacao.historicoTitle}</h2>
            <HistoricoView
              historico={historico}
              loading={histLoading}
              error={histError}
              onRetry={() => void refreshHistorico()}
            />
          </div>

          <NovaVersaoForm
            bancoId={selectedBanco}
            onCreated={handleCreated}
            onSubmittingChange={setSalvando}
          />
        </>
      )}
    </section>
  );
}

export default PrecificacaoPage;
